import { Observable, throwError, ObservableInput } from 'rxjs';
import { HttpClient, HttpHeaders, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { catchError, map, delay } from 'rxjs/operators';
import { Deserializable } from '../model/deserializable';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import firebase from 'firebase/compat/app';
import { Log } from '../util/log';
import { environment } from 'src/environments/environment';
import { AngularFireModule } from '@angular/fire/compat';


export class BaseService {

  public context: string;

  private user: firebase.User;
  private userUid: string;

  protected workerAuth: firebase.auth.Auth;

  constructor(private http: HttpClient, public afAuth: AngularFireAuth) {

    this.context = environment.onecontrol.context;

    this.afAuth.user.subscribe(user => {

      // Log.debug('user: ' + JSON.stringify(user));
      if (user) {
        this.user = user;
        this.userUid = user.uid;
      } else {
      //  console.log("Not logged in");
      }
    });
  }

  public getFirebaseUserObs() {

    return this.afAuth.user;
  }

  public setFirebaseUser(user) {

    this.user = user;
    this.userUid = user.uid;
  }

  public getFirebaseUid(): string {

    return this.userUid;
  }

  public getFirebaseEmail(): string {

    return this.user.email;
  }

  public isFirebaseLogged(): boolean {

    return this.user != null;
  }

  public getToken() {
    return this.afAuth.idToken;
  }

  public getFirebaseUser() {

    return this.user;
  }

  getData<T extends Deserializable>(path): Observable<T> {

    return new Observable<T>(observer => {

      let tokenSubscription = this.getToken().subscribe(token => {

        tokenSubscription.unsubscribe();

        // Log.debug('token: ' + token);

        let params = new HttpParams().set('key', environment.onecontrol.apiKey);

        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + token,
            'observe': 'response',
            'X-control-context': this.context,
            'X-control-version': environment.onecontrol.version
          }),
          params: params
        };

        Log.debug('getting: ' + environment.onecontrol.apiUrl + path);

        return this.http.get<T>(environment.onecontrol.apiUrl + path, httpOptions)/*.pipe(delay(2500))*/
          .pipe(catchError((error: HttpErrorResponse) => {

            Log.debug('error: ' + JSON.stringify(error.error));

            observer.error(error.error);
            observer.complete();

            return throwError('error');
          }))
          .pipe(map(res => {

            // Log.debug('get: ' + JSON.stringify(res));

            observer.next(res as T);
            observer.complete();
          })).subscribe();

      }, error => {

        Log.debug('error: ' + JSON.stringify(error));

        observer.error(error);
        observer.complete();
      });

    });
  }

  getPublicData<T extends Deserializable>(path): Observable<T> {

    // https://onecontrol-cloud-pilomat.appspot.com/_ah/api/public/v1/

    

    return new Observable<T>(observer => {

      let tokenSubscription = this.getToken().subscribe(token => {

        tokenSubscription.unsubscribe();

        Log.debug('token: ' + token);

        let params = new HttpParams().set('key', environment.onecontrol.apiKey);

        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            // 'Authorization': 'Bearer ' + token,
            'observe': 'response',
            'X-control-context': this.context,
            'X-control-version': environment.onecontrol.version
          }),
          params: params
        };

        Log.debug('getting: ' + "https://onecontrol-cloud-pilomat.appspot.com/_ah/api/public/v1/" + path);

        return this.http.get<T>("https://onecontrol-cloud-pilomat.appspot.com/_ah/api/public/v1/" + path, httpOptions)/*.pipe(delay(2500))*/
          .pipe(catchError((error: HttpErrorResponse) => {

            Log.debug('error: ' + JSON.stringify(error.error));

            observer.error(error.error);
            observer.complete();

            return throwError('error');
          }))
          .pipe(map(res => {

            // Log.debug('get: ' + JSON.stringify(res));

            observer.next(res as T);
            observer.complete();
          })).subscribe();

      }, error => {

        Log.debug('error: ' + JSON.stringify(error));

        observer.error(error);
        observer.complete();
      });

    });
  }

  getDataApi<T extends Deserializable>(path): Observable<T> {

    return new Observable<T>(observer => {

        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'observe': 'response',
            'Access-Control-Allow-Origin' :  '*'

          })
        };

        Log.debug('getting: ' + path);

        return this.http.get<T>(path)
          .pipe(catchError((error: HttpErrorResponse) => {

            Log.debug('error: ' + JSON.stringify(error.error));

            observer.error(error.error);
            observer.complete();

            return throwError('error');
          }))
          .pipe(map(res => {
            observer.next(res as T);
            observer.complete();
          })).subscribe();
    });
  }

  postData<T>(path, data): Observable<T> {

    return new Observable<T>(observer => {

      let tokenSubscription = this.getToken().subscribe(token => {

        tokenSubscription.unsubscribe();

        let params = new HttpParams().set('key', environment.onecontrol.apiKey);

        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + token,
            'observe': 'response',
            'X-control-context': this.context,
            'X-control-version': environment.onecontrol.version
          }),
          params: params
        };

        Log.debug('posting: ' + environment.onecontrol.apiUrl + path);

        return this.http.post<T>(environment.onecontrol.apiUrl + path, data, httpOptions)/*.pipe(delay(2500))*/
          .pipe(catchError((error: HttpErrorResponse) => {

            Log.debug('error: ' + JSON.stringify(error.error));

            observer.error(error.error);
            observer.complete();

            return throwError('error');
          }))
          .pipe(map(res => {

            Log.debug('post: ' + JSON.stringify(res));

            observer.next(res);
            observer.complete();
          })).subscribe();

      }, error => {

        Log.debug('error: ' + JSON.stringify(error));

        observer.error(error);
        observer.complete();
      });
    });
  }

  postPublicData<T>(path, data): Observable<T> {

      // https://onecontrol-cloud-pilomat.appspot.com/_ah/api/public/v1/


    return new Observable<T>(observer => {

      let tokenSubscription = this.getToken().subscribe(token => {

        tokenSubscription.unsubscribe();

        let params = new HttpParams().set('key', environment.onecontrol.apiKey);

        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            // 'Authorization': 'Bearer ' + token,
            'observe': 'response',
            'X-control-context': this.context,
            'X-control-version': environment.onecontrol.version
          }),
          params: params
        };

        Log.debug('posting: ' + "https://onecontrol-cloud-pilomat.appspot.com/_ah/api/public/v1/" + path);

        return this.http.post<T>("https://onecontrol-cloud-pilomat.appspot.com/_ah/api/public/v1/" + path, data, httpOptions)/*.pipe(delay(2500))*/
          .pipe(catchError((error: HttpErrorResponse) => {

            Log.debug('error: ' + JSON.stringify(error.error));

            observer.error(error.error);
            observer.complete();

            return throwError('error');
          }))
          .pipe(map(res => {

            Log.debug('post: ' + JSON.stringify(res));

            observer.next(res);
            observer.complete();
          })).subscribe();

      }, error => {

        Log.debug('error: ' + JSON.stringify(error));

        observer.error(error);
        observer.complete();
      });
    });
  }

  postDataApi<T extends Deserializable>(path,key, data): Observable<T> {

    return new Observable<T>(observer => {

        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'observe': 'response',
          })
        };

        Log.debug('getting: ' + path);

        return this.http.post<T>(path+"?"+key+"="+data,null)
          .pipe(catchError((error: HttpErrorResponse) => {

            Log.debug('error: ' + JSON.stringify(error.error));

            observer.error(error.error);
            observer.complete();

            return throwError('error');
          }))
          .pipe(map(res => {
            observer.next(res as T);
            observer.complete();
          })).subscribe();
    });
  }

  putData<T>(path, data): Observable<T> {

    return new Observable<T>(observer => {

      let tokenSubscription = this.getToken().subscribe(token => {

        tokenSubscription.unsubscribe();

        let params = new HttpParams().set('key', environment.onecontrol.apiKey);

        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + token,
            'observe': 'response',
            'X-control-context': this.context,
            'X-control-version': environment.onecontrol.version
          }),
          params: params
        };

        Log.debug('putting: ' + environment.onecontrol.apiUrl + path);

        return this.http.put<T>(environment.onecontrol.apiUrl + path, data, httpOptions)/*.pipe(delay(2500))*/
          .pipe(catchError((error: HttpErrorResponse) => {

            Log.debug('error: ' + JSON.stringify(error.error));

            observer.error(error.error);
            observer.complete();

            return throwError('error');
          }))
          .pipe(map(res => {

            Log.debug('post: ' + JSON.stringify(res));

            observer.next(res);
            observer.complete();
          })).subscribe();

      }, error => {

        Log.debug('error: ' + JSON.stringify(error));

        observer.error(error);
        observer.complete();
      });
    });
  }

  deleteData<T>(path): Observable<T> {

    return new Observable<T>(observer => {

      let tokenSubscription = this.getToken().subscribe(token => {

        tokenSubscription.unsubscribe();

        let params = new HttpParams().set('key', environment.onecontrol.apiKey);

        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + token,
            'observe': 'response',
            'X-control-context': this.context,
            'X-control-version': environment.onecontrol.version
          }),
          params: params
        };

        Log.debug('deleting: ' + environment.onecontrol.apiUrl + path);

        return this.http.delete<T>(environment.onecontrol.apiUrl + path, httpOptions)/*.pipe(delay(2500))*/
          .pipe(catchError((error: HttpErrorResponse) => {

            Log.debug('error: ' + JSON.stringify(error.error));

            observer.error(error.error);
            observer.complete();

            return throwError('error');
          }))
          .pipe(map(res => {

            Log.debug('delete: ' + JSON.stringify(res));

            observer.next(res);
            observer.complete();
          })).subscribe();

      }, error => {

        Log.debug('error: ' + JSON.stringify(error));

        observer.error(error);
        observer.complete();
      });
    });
  }

  getUrl(url): Observable<any> {

    Log.debug('getting: ' + url);

    return this.http.get(url);
  }


  // private handleHttpError(error: HttpErrorResponse) {

  //   if (error.error instanceof ErrorEvent) {
  //     // A client-side or network error occurred. Handle it accordingly.
  //     console.error('An error occurred:', error.error.message);
  //   } else {
  //     // The backend returned an unsuccessful response code.
  //     // The response body may contain clues as to what went wrong,
  //     console.error(
  //       `Backend returned code ${error.status}, ` +
  //       `body was: ${error.error}`);
  //   }
  //   // return an observable with a user-facing error message
  //   return throwError('Something bad happened; please try again later.');
  // }

}
